import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Loader2, Calendar, CalendarCheck, ExternalLink } from 'lucide-react';
import { streamFunnelChat } from '@/services/chatApi';
import { renderMarkdownBold } from '@/utils/renderMarkdown';
import { BACKEND_URL } from '@/config/api';

const API = BACKEND_URL;
const BOOK_MARKER = '[[MOSTRAR_AGENDA]]';
// Agenda de llamadas del equipo (Google Calendar). El backend puede sobrescribirla desde la
// configuración del funnel (booking_url); esta es la de respaldo si la config no responde.
const DEFAULT_BOOKING_URL = 'https://calendly.com/toni-freedomhomes/30min';  // agenda de Toni (02/11/2026)

/*
  FreedySDRChat — el "SDR de IA" que cualifica y agenda tras el quiz.

  Reutilizable en cualquier funnel. Recibe un leadId ya creado/puntuado y:
    1. Auto-dispara un saludo personalizado de Freedy (seed oculto).
    2. Conversa en streaming (mismo contrato SSE que el resto de chats).
    3. Detecta el marcador [[MOSTRAR_AGENDA]] → muestra el botón «Elegir día y hora».
    4. El botón abre la agenda de Google Calendar en otra pestaña y registra el clic como
       INTENCIÓN de agendar (booking-click). Google Calendar no avisa de la reserva, así que
       nunca afirmamos que la llamada está reservada: eso lo confirma el equipo en el panel.

  El marcador NUNCA se muestra al usuario — se limpia del texto en pantalla.
*/

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isUser ? 'bg-[#D4AF37] text-[#0F172A] font-medium rounded-br-sm'
                 : 'bg-white/[0.06] text-gray-100 border border-white/10 rounded-bl-sm'
        }`}
        style={{ whiteSpace: 'pre-wrap' }}
      >
        {msg.content ? renderMarkdownBold(msg.content) : <span className="opacity-50 animate-pulse">▌</span>}
      </div>
    </div>
  );
}

// leadName, prefillEmail, temperature y affiliateName se aceptan por compatibilidad (la agenda de Google
// no admite datos precargados de forma fiable).
// bookingInParent: el contenedor (QualificationForm) ya muestra SIEMPRE el botón de agenda arriba; aquí no se
// duplica: el marcador de agenda solo se limpia del texto. compact: zona de mensajes más baja (móvil).
export default function FreedySDRChat({ leadId, bookingInParent = false, compact = false }) {
  // messages may carry { hidden:true } — sent to the API for context but not rendered.
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingUrl, setBookingUrl] = useState(DEFAULT_BOOKING_URL);
  const [bookingOpened, setBookingOpened] = useState(false);
  const endRef = useRef(null);
  const startedRef = useRef(false);

  const visible = messages.filter((m) => !m.hidden);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showBooking, bookingOpened]);

  // Enlace de la agenda desde la config del funnel (booking_url; calendly_url por compatibilidad)
  useEffect(() => {
    axios.get(`${API}/api/funnel/config`)
      .then((r) => {
        const url = r.data?.booking_url || r.data?.calendly_url;
        if (url && /^https:\/\//.test(url)) setBookingUrl(url);
      })
      .catch(() => {});
  }, []);

  const runStream = useCallback((history) => {
    setLoading(true);
    // Placeholder assistant bubble that fills as chunks arrive.
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
    let raw = '';
    streamFunnelChat(leadId, history.map((m) => ({ role: m.role, content: m.content })), {
      onChunk: (chunk) => {
        raw += chunk;
        if (raw.includes(BOOK_MARKER) && !bookingInParent) setShowBooking(true);
        const clean = raw.split(BOOK_MARKER).join('').trim();
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { role: 'assistant', content: clean };
          return u;
        });
      },
      onDone: () => setLoading(false),
      onError: () => {
        setMessages((prev) => {
          const u = [...prev];
          u[u.length - 1] = { role: 'assistant', content: 'Disculpa, tuve un problema. ¿Puedes repetirlo?' };
          return u;
        });
        setLoading(false);
      },
    });
  }, [leadId, bookingInParent]);

  // Auto-fire Freedy's personalised opener once.
  useEffect(() => {
    if (startedRef.current || !leadId) return;
    startedRef.current = true;
    const seed = { role: 'user', content: 'Hola, acabo de completar el test de idoneidad.', hidden: true };
    setMessages([seed]);
    runStream([seed]);
  }, [leadId, runStream]);

  const sendMessage = () => {
    const content = input.trim();
    if (!content || loading) return;
    const userMsg = { role: 'user', content };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    runStream(history);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // El enlace abre la agenda en otra pestaña (lo hace el propio <a>); aquí solo se registra la intención.
  const onBookingClick = () => {
    setBookingOpened(true);
    if (leadId) axios.post(`${API}/api/funnel/lead/${leadId}/booking-click`).catch(() => {});
  };

  return (
    <div className="flex flex-col" data-testid="freedy-sdr-chat">
      {/* Header */}
      <div className="flex items-center gap-3 px-1 pb-3 mb-1 border-b border-white/10">
        <div className="w-9 h-9 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0F172A] font-bold">F</div>
        <div>
          <p className="text-white font-semibold text-sm leading-tight">Freedy</p>
          <p className="text-[11px] text-emerald-400 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> En línea
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className={`flex-1 overflow-y-auto px-1 py-3 ${compact ? 'max-h-[34vh] min-h-[150px]' : 'max-h-[40vh] min-h-[200px]'}`}>
        {visible.map((msg, i) => <MessageBubble key={i} msg={msg} />)}

        {/* Agenda (Google Calendar, en pestaña nueva) */}
        {showBooking && (
          <div className="my-3 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.07] p-4" data-testid="sdr-booking">
            <p className="flex items-center gap-2 text-sm font-medium text-white">
              <Calendar size={15} className="text-[#D4AF37] flex-shrink-0" /> Reserva tu llamada con el equipo
            </p>
            <p className="mt-1 text-xs text-white/55 leading-relaxed">Se abrirá la agenda en otra pestaña. Elige el hueco que mejor te venga.</p>
            <a href={bookingUrl} target="_blank" rel="noopener noreferrer" onClick={onBookingClick} data-testid="sdr-booking-btn"
              className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#D4AF37] px-4 text-sm font-semibold text-[#0F172A] hover:bg-[#C5A028] transition-colors">
              <CalendarCheck size={17} /> Elegir día y hora <ExternalLink size={14} className="opacity-70" />
            </a>
            {bookingOpened && (
              <p className="mt-3 text-xs text-white/70 leading-relaxed" data-testid="sdr-booking-opened">
                Te hemos abierto la agenda en otra pestaña. Cuando reserves, recibirás la confirmación por email. Si no se ha abierto, pulsa de nuevo el botón.
              </p>
            )}
          </div>
        )}

        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="px-1 pt-2 border-t border-white/10">
        <div className="flex items-end gap-2 bg-white/[0.06] rounded-xl px-3 py-2 border border-white/10 focus-within:border-[#D4AF37]/40">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Escribe tu mensaje a Freedy..."
            disabled={loading}
            rows={1}
            className="flex-1 bg-transparent text-white text-sm placeholder-gray-500 resize-none focus:outline-none py-1"
            style={{ maxHeight: '80px' }}
            data-testid="sdr-input"
          />
          <button
            type="button"
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            aria-label="Enviar"
            className="w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center bg-[#D4AF37] disabled:opacity-30"
          >
            {loading ? <Loader2 size={14} className="animate-spin text-[#0F172A]" /> : <Send size={14} className="text-[#0F172A]" />}
          </button>
        </div>
      </div>
    </div>
  );
}
